import React, { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Button, Checkbox, Input, Modal, Select } from 'antd'
import styled from 'styled-components/macro'
import { BodyText, H3 } from 'src/branding/typography'
import {
  FoodSafetyHeroBand,
  FoodSafetyPageTitle,
  FoodSafetySurfaceCard,
  FoodSafetySubmitErrorBox,
} from 'src/components/food-safety/FoodSafetyPageChrome'
import Loading from 'src/components/styled/Loading'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import useIsBlocked from 'src/hooks/useIsBlocked'
import { createFoodSafetySubmissionKey } from 'src/utils/foodSafetySaveGuard'
import {
  fetchFoodSafetyVisitors,
  fetchFoodSafetyVisitorDeclaration,
  saveFoodSafetyVisitorPolicy,
  signInFoodSafetyVisitor,
  signOutFoodSafetyVisitor,
} from 'src/utils/foodSafetyApi'

const emptySignIn = () => ({
  submissionKey: createFoodSafetySubmissionKey(),
  kind: 'visitor',
  name: '',
  company: '',
  purpose: '',
  answers: {},
  acknowledged: false,
  acknowledgedByName: '',
})
const displayTime = value =>
  value
    ? new Date(value).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Not signed out'

const FoodSafetyVisitorsScreen = () => {
  const { venueId } = useParams()
  const blocked = useIsBlocked('food-safety')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [refresh, setRefresh] = useState(0)
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState({})
  const [dateDraft, setDateDraft] = useState({
    from: '',
    to: '',
    onSite: false,
  })
  const [modal, setModal] = useState(null)
  const [draft, setDraft] = useState(emptySignIn)
  const [formPolicy, setFormPolicy] = useState(null)
  const [formError, setFormError] = useState('')
  const [busy, setBusy] = useState(false)
  const [declaration, setDeclaration] = useState(null)
  const [opening, setOpening] = useState(null)
  const lock = useRef(false)
  const generation = useRef(0)
  const questionSequence = useRef(0)
  const declarationRequest = useRef(0)

  useEffect(() => {
    generation.current += 1
    setData(null)
    setModal(null)
    setDeclaration(null)
    setDraft(emptySignIn())
    setPage(1)
    setFilters({})
    setDateDraft({ from: '', to: '', onSite: false })
    return () => {
      generation.current += 1
    }
  }, [venueId, blocked])

  useEffect(() => {
    if (blocked || !venueId) return undefined
    let active = true
    setLoading(true)
    setError('')
    fetchFoodSafetyVisitors(venueId, { ...filters, page })
      .then(result => {
        if (active) setData(result)
        return null
      })
      .finally(() => {
        if (active) setLoading(false)
      })
      .catch(err => {
        if (active) setError(err.message)
      })
    return () => {
      active = false
    }
  }, [venueId, blocked, filters, page, refresh])

  useEffect(() => {
    const focus = () => setRefresh(value => value + 1)
    window.addEventListener('focus', focus)
    return () => window.removeEventListener('focus', focus)
  }, [])

  const closeModal = () => {
    if (busy) return
    setModal(null)
    setDraft(emptySignIn())
    setFormPolicy(null)
    setFormError('')
  }
  const openSignIn = () => {
    setDraft(emptySignIn())
    setFormPolicy(data.policy)
    setFormError('')
    setModal('sign-in')
  }
  const openSettings = () => {
    setDraft({
      ...data.policy,
      retentionDays: data.policy.retentionDays || '',
      questions: data.policy.questions.map(question => ({ ...question })),
    })
    setFormError('')
    setModal('settings')
  }
  const patch = values => setDraft(previous => ({ ...previous, ...values }))
  const runWrite = async task => {
    if (lock.current) return
    lock.current = true
    setBusy(true)
    const current = generation.current
    try {
      await task()
      if (generation.current === current) {
        setModal(null)
        setDraft(emptySignIn())
        setFormPolicy(null)
        setPage(1)
        setRefresh(value => value + 1)
      }
    } catch (err) {
      if (generation.current === current) {
        if (modal) setFormError(err.message)
        else setError(err.message)
      }
    } finally {
      lock.current = false
      setBusy(false)
    }
  }
  const save = () => {
    setFormError('')
    if (modal === 'settings') {
      return runWrite(() => saveFoodSafetyVisitorPolicy(venueId, draft))
    }
    if (!draft.name.trim() || !draft.purpose.trim()) {
      setFormError('Enter the visitor name and purpose of visit.')
      return undefined
    }
    if (
      formPolicy.questions.some(
        question => typeof draft.answers[question.key] !== 'boolean',
      )
    ) {
      setFormError('Answer every declaration question with Yes or No.')
      return undefined
    }
    if (
      !draft.acknowledged ||
      draft.acknowledgedByName.trim().replace(/\s+/g, ' ').toLowerCase() !==
        draft.name.trim().replace(/\s+/g, ' ').toLowerCase()
    ) {
      setFormError(
        'The visitor must acknowledge the declaration using their name.',
      )
      return undefined
    }
    return runWrite(() =>
      signInFoodSafetyVisitor(venueId, {
        ...draft,
        policyRevision: formPolicy.revision,
        answers: formPolicy.questions.map(question => ({
          key: question.key,
          answer: draft.answers[question.key],
        })),
      }),
    )
  }
  const openDeclaration = async visit => {
    const current = generation.current
    const request = ++declarationRequest.current
    const isCurrent = () =>
      generation.current === current && declarationRequest.current === request
    setOpening(visit._id)
    setError('')
    try {
      const result = await fetchFoodSafetyVisitorDeclaration(venueId, visit._id)
      if (isCurrent()) setDeclaration(result.visit)
    } catch (err) {
      if (isCurrent()) setError(err.message)
    } finally {
      if (isCurrent()) setOpening(null)
    }
  }
  const applyFilters = () => {
    if (
      (dateDraft.from || dateDraft.to) &&
      (!dateDraft.from || !dateDraft.to || dateDraft.from > dateDraft.to)
    ) {
      setError('Choose a valid arrival date range.')
      return
    }
    setPage(1)
    setFilters({
      ...(dateDraft.from ? { from: dateDraft.from, to: dateDraft.to } : {}),
      ...(dateDraft.onSite ? { onSite: '1' } : {}),
    })
  }
  const updateQuestion = (index, values) =>
    patch({
      questions: draft.questions.map((question, i) =>
        i === index ? { ...question, ...values } : question,
      ),
    })

  if (blocked) return <PermissionBlockScreen />
  return (
    <Wrapper>
      <Link to={`/v/${venueId}/food-safety`}>‹ Today’s overview</Link>
      <FoodSafetyHeroBand>
        <FoodSafetyPageTitle>Contractors &amp; Visitors</FoodSafetyPageTitle>
        <BodyText>
          Record attendance, the visitor’s declaration and acknowledgement. No
          visitor login is required.
        </BodyText>
      </FoodSafetyHeroBand>
      {error && (
        <FoodSafetySubmitErrorBox role='alert'>
          {error}
          <Button onClick={() => setRefresh(value => value + 1)}>Retry</Button>
        </FoodSafetySubmitErrorBox>
      )}
      {loading && !data && <Loading />}
      {data && (
        <>
          <Card>
            <H3>Visitor log</H3>
            <BodyText>
              Times shown in your local timezone. Arrival and departure times
              are recorded when you sign in or sign out.
            </BodyText>
            <BodyText>
              Attendance records are available to authorised Food Safety staff.
              Saved declaration answers are restricted to users who can manage
              Food Safety.
            </BodyText>
            {data.policy.retentionDays ? (
              <BodyText>
                Records are retained for {data.policy.retentionDays} days from
                arrival, or their earlier recorded expiry date.
              </BodyText>
            ) : (
              <BodyText>
                A manager must configure the questionnaire and retention period
                before the first sign-in.
              </BodyText>
            )}
            <Actions>
              <Button
                type='primary'
                disabled={!data.policy.retentionDays || busy}
                onClick={openSignIn}
              >
                Sign in visitor / contractor
              </Button>
              {data.canManage && (
                <Button onClick={openSettings} disabled={busy}>
                  Configure questionnaire &amp; retention
                </Button>
              )}
              <Button
                disabled={loading}
                onClick={() => setRefresh(value => value + 1)}
              >
                Refresh log
              </Button>
            </Actions>
            <Actions>
              <Field>
                Arrivals from
                <Input
                  aria-label='Arrivals from'
                  type='date'
                  value={dateDraft.from}
                  onChange={event =>
                    setDateDraft(previous => ({
                      ...previous,
                      from: event.target.value,
                    }))
                  }
                />
              </Field>
              <Field>
                To
                <Input
                  aria-label='Arrivals to'
                  type='date'
                  value={dateDraft.to}
                  onChange={event =>
                    setDateDraft(previous => ({
                      ...previous,
                      to: event.target.value,
                    }))
                  }
                />
              </Field>
              <Checkbox
                checked={dateDraft.onSite}
                onChange={event =>
                  setDateDraft(previous => ({
                    ...previous,
                    onSite: event.target.checked,
                  }))
                }
              >
                On site only
              </Checkbox>
              <Button onClick={applyFilters}>Apply filters</Button>
              <Button
                onClick={() => {
                  setDateDraft({ from: '', to: '', onSite: false })
                  setFilters({})
                  setPage(1)
                }}
              >
                Clear filters
              </Button>
            </Actions>
            {!data.visits.length && (
              <BodyText>
                No visits found within the retention period and selected
                filters.
              </BodyText>
            )}
            {data.visits.map(visit => (
              <Visit key={visit._id}>
                <H3>
                  {visit.name} · {visit.departedAt ? 'Signed out' : 'On site'}
                </H3>
                <p>
                  {visit.kind === 'contractor' ? 'Contractor' : 'Visitor'}
                  {visit.company ? ` · ${visit.company}` : ''}
                </p>
                <p>
                  <strong>Purpose:</strong> {visit.purpose}
                </p>
                <p>
                  <strong>Arrival:</strong> {displayTime(visit.arrivedAt)}
                  <br />
                  <strong>Departure:</strong> {displayTime(visit.departedAt)}
                </p>
                <p>
                  Declaration completed · Acknowledged by{' '}
                  {visit.acknowledgedByName} ·{' '}
                  {displayTime(visit.acknowledgedAt)}
                </p>
                <Small>
                  Recorded by {visit.recordedByName}
                  {visit.departureRecordedByName
                    ? ` · Signed out by ${visit.departureRecordedByName}`
                    : ''}
                </Small>
                <Actions>
                  {!visit.departedAt && (
                    <Button
                      disabled={busy}
                      onClick={() =>
                        runWrite(() =>
                          signOutFoodSafetyVisitor(venueId, visit._id),
                        )
                      }
                    >
                      Sign out {visit.name}
                    </Button>
                  )}
                  {data.canManage && (
                    <Button
                      loading={opening === visit._id}
                      onClick={() => openDeclaration(visit)}
                    >
                      View declaration
                    </Button>
                  )}
                </Actions>
              </Visit>
            ))}
            <Actions>
              <Button
                disabled={page === 1 || loading}
                onClick={() => setPage(value => value - 1)}
              >
                Previous
              </Button>
              <span>Page {page}</span>
              <Button
                disabled={!data.hasMore || loading}
                onClick={() => setPage(value => value + 1)}
              >
                Next
              </Button>
            </Actions>
          </Card>
        </>
      )}
      <Modal
        visible={Boolean(modal)}
        title={
          modal === 'settings'
            ? 'Visitor questionnaire & retention'
            : 'Sign in visitor / contractor'
        }
        onCancel={closeModal}
        onOk={save}
        confirmLoading={busy}
        okText={modal === 'settings' ? 'Save settings' : 'Record sign-in'}
        destroyOnClose
      >
        {formError && (
          <FoodSafetySubmitErrorBox role='alert'>
            {formError}
          </FoodSafetySubmitErrorBox>
        )}
        {modal === 'sign-in' && formPolicy && (
          <>
            <BodyText>
              This log is used for site hygiene and inspection records.
              Retention: {formPolicy.retentionDays} days from arrival. Do not
              enter medical details.
            </BodyText>
            <Field>
              Visitor or contractor
              <Select
                aria-label='Visit type'
                value={draft.kind}
                onChange={kind => patch({ kind })}
                style={{ width: '100%' }}
              >
                <Select.Option value='visitor'>Visitor</Select.Option>
                <Select.Option value='contractor'>Contractor</Select.Option>
              </Select>
            </Field>
            <Field>
              Visitor name
              <Input
                aria-label='Visitor name'
                maxLength={200}
                value={draft.name}
                onChange={event => patch({ name: event.target.value })}
              />
            </Field>
            <Field>
              Organisation / company (optional)
              <Input
                aria-label='Company'
                maxLength={200}
                value={draft.company}
                onChange={event => patch({ company: event.target.value })}
              />
            </Field>
            <Field>
              Purpose of visit
              <Input
                aria-label='Purpose of visit'
                maxLength={300}
                value={draft.purpose}
                onChange={event => patch({ purpose: event.target.value })}
              />
            </Field>
            <H3>Visitor declaration</H3>
            {formPolicy.questions.map(question => (
              <Question key={question.key}>
                <p>{question.text}</p>
                <Actions>
                  {[true, false].map(answer => (
                    <Button
                      key={String(answer)}
                      aria-label={`${question.text} ${answer ? 'Yes' : 'No'}`}
                      type={
                        draft.answers[question.key] === answer
                          ? 'primary'
                          : 'default'
                      }
                      onClick={() =>
                        patch({
                          answers: { ...draft.answers, [question.key]: answer },
                        })
                      }
                    >
                      {answer ? 'Yes' : 'No'}
                    </Button>
                  ))}
                </Actions>
              </Question>
            ))}
            {formPolicy.questions.some(
              question =>
                question.expectedAnswer != null &&
                typeof draft.answers[question.key] === 'boolean' &&
                draft.answers[question.key] !== question.expectedAnswer,
            ) && (
              <BodyText>
                Ask the host to review these answers before entering
                food-handling areas. This log records attendance, not permission
                to enter.
              </BodyText>
            )}
            <Checkbox
              checked={draft.acknowledged}
              onChange={event => patch({ acknowledged: event.target.checked })}
            >
              {formPolicy.acknowledgement}
            </Checkbox>
            <Field>
              Visitor acknowledgement name
              <Input
                aria-label='Visitor acknowledgement name'
                maxLength={200}
                value={draft.acknowledgedByName}
                onChange={event =>
                  patch({ acknowledgedByName: event.target.value })
                }
              />
            </Field>
            <Small>
              The visitor must provide the answers and acknowledgement; venue
              staff record the sign-in.
            </Small>
          </>
        )}
        {modal === 'settings' && (
          <>
            <BodyText>
              Use only the yes/no questions needed for your premises. Do not
              request diagnoses, medication or medical history.
            </BodyText>
            <Field>
              Retention period (days)
              <Input
                aria-label='Retention period in days'
                type='number'
                min={1}
                max={3650}
                value={draft.retentionDays}
                onChange={event => patch({ retentionDays: event.target.value })}
              />
            </Field>
            <Small>
              Choose the period required by your business procedure. Shorter
              periods also apply to existing visits; longer periods only apply
              to new visits. Expired visits are unavailable and automatically
              removed.
            </Small>
            {draft.questions?.map((question, index) => (
              <Question key={question.key}>
                <Field>
                  Question {index + 1}
                  <Input
                    aria-label={`Question ${index + 1}`}
                    maxLength={300}
                    value={question.text}
                    onChange={event =>
                      updateQuestion(index, { text: event.target.value })
                    }
                  />
                </Field>
                <Field>
                  Expected answer
                  <Select
                    aria-label={`Expected answer ${index + 1}`}
                    style={{ width: '100%' }}
                    value={
                      question.expectedAnswer == null
                        ? 'any'
                        : question.expectedAnswer
                        ? 'yes'
                        : 'no'
                    }
                    onChange={value =>
                      updateQuestion(index, {
                        expectedAnswer:
                          value === 'any' ? null : value === 'yes',
                      })
                    }
                  >
                    <Select.Option value='yes'>Yes</Select.Option>
                    <Select.Option value='no'>No</Select.Option>
                    <Select.Option value='any'>
                      No expected answer
                    </Select.Option>
                  </Select>
                </Field>
                <Button
                  disabled={draft.questions.length === 1}
                  onClick={() =>
                    patch({
                      questions: draft.questions.filter((q, i) => i !== index),
                    })
                  }
                >
                  Remove question {index + 1}
                </Button>
              </Question>
            ))}
            <Button
              disabled={draft.questions?.length >= 20}
              onClick={() => {
                questionSequence.current += 1
                patch({
                  questions: [
                    ...draft.questions,
                    {
                      key: `question_${Date.now()}_${questionSequence.current}`,
                      text: '',
                      expectedAnswer: null,
                    },
                  ],
                })
              }}
            >
              Add question
            </Button>
            <Field>
              Acknowledgement text
              <Input.TextArea
                aria-label='Acknowledgement text'
                rows={3}
                maxLength={500}
                value={draft.acknowledgement}
                onChange={event =>
                  patch({ acknowledgement: event.target.value })
                }
              />
            </Field>
            <Small>
              Completed visits retain their original questionnaire and
              acknowledgement.
            </Small>
          </>
        )}
      </Modal>
      <Modal
        visible={Boolean(declaration)}
        title='Visitor declaration'
        footer={<Button onClick={() => setDeclaration(null)}>Close</Button>}
        onCancel={() => setDeclaration(null)}
        destroyOnClose
      >
        {declaration && (
          <>
            <H3>{declaration.name}</H3>
            <p>
              {declaration.company} · {declaration.purpose}
            </p>
            <p>Arrival: {displayTime(declaration.arrivedAt)}</p>
            {declaration.needsReview && (
              <p>
                <strong>
                  Host review needed: at least one answer differs from the
                  venue’s expected answer.
                </strong>
              </p>
            )}
            {declaration.declaration.map(question => (
              <Question key={question.key}>
                <p>{question.text}</p>
                <strong>{question.answer ? 'Yes' : 'No'}</strong>
              </Question>
            ))}
            <p>{declaration.acknowledgement}</p>
            <p>
              Acknowledged by {declaration.acknowledgedByName} ·{' '}
              {displayTime(declaration.acknowledgedAt)}
            </p>
            <Small>
              Questionnaire version {declaration.policyRevision} · Recorded by{' '}
              {declaration.recordedByName}
              <br />
              Expiry: {displayTime(declaration.expiresAt)}
            </Small>
          </>
        )}
      </Modal>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  max-width: 1060px;
  margin: 2rem auto;
  padding: 0 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`
const Card = styled(FoodSafetySurfaceCard)`
  padding: 1.25rem;
`
const Field = styled.label`
  display: block;
  margin: 0.7rem 0;
  font-weight: 600;
`
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem;
  margin: 0.8rem 0;
`
const Small = styled.div`
  font-size: 0.85rem;
  color: #595959;
  overflow-wrap: anywhere;
`
const Visit = styled.div`
  padding: 1rem 0;
  border-top: 1px solid #d9d9d9;
  overflow-wrap: anywhere;
`
const Question = styled.div`
  margin: 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e8e8e8;
`

export default FoodSafetyVisitorsScreen
